import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS, services as SVC } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useNav } from '../../../navigation.js';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { askPeriod, formatNumber, periodRangeLabel } from '../analyticsRange.js';
import { isWithheld, measure, NOT_MEASURED_LABEL, withheldReason } from '../disclosure.js';
import { DrillPanel, WeeklyPanel, type Drill, TrendFailure } from '../drilldown.js';
import { METRIC_ABOUT } from '../explain.js';
import {
  DisclosureNote,
  DisclosureStrip,
  DrilldownTable,
  MeasureStat,
  NotMeasuredStat,
  Panel,
  Stat,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';
import { CONFLICT_DETAIL, REASON_CODE_LABELS } from '../../../schedule/scheduleBoard.js';

type StaffWorkerRow = DashboardNS.StaffWorkerRow;

const hours = (v: number) => formatNumber(v, 1);

/** Every reason code a delivered visit can carry, in words. Exhaustive by type. */
const DELIVERED_REASON_LABELS: Record<SVC.DeliveredReasonCode, string> = {
  ...REASON_CODE_LABELS,
  ENCOUNTER_WITHOUT_OCCURRENCE: 'Documented in the EHR with no calendar event',
  OCCURRENCE_WITHOUT_ENCOUNTER: 'No encounter was documented for it',
  NO_EVENT_CANDIDATE: 'No calendar event it could belong to',
  MULTIPLE_EVENT_CANDIDATES: 'More than one calendar event it could belong to',
  SOURCE_STATUS_CONFLICT: CONFLICT_DETAIL,
  OUTSIDE_OCCURRENCE_COVERAGE: 'From before the EHR calendar was connected',
};

/**
 * `?tab=schedule&from=&to=` — the worker's schedule over THIS period (`to`
 * exclusive, as the server sends it), so a 30-day row opens their 30 days.
 */
function workerScheduleHref(workerId: string, period: DashboardNS.PeriodLabel): string {
  const q = new URLSearchParams({ tab: 'schedule' });
  if (period.from) q.set('from', period.from);
  q.set('to', period.to);
  return `/people/${encodeURIComponent(workerId)}?${q.toString()}`;
}

/**
 * Why utilization has no figure, in words. It is never 0%: without a time
 * clock there are no worked hours, and without contracted hours there is
 * nothing to be a percentage of.
 */
const UTILIZATION_REASON: Record<DashboardNS.UtilizationUnavailableReason, string> = {
  no_source: 'No time clock is connected, so there are no worked hours to compare.',
  expected_hours_not_on_file:
    'No worker has contracted weekly hours on file, so there is nothing to compare against.',
  expected_hours_not_positive:
    'Contracted weekly hours on file are zero, so there is nothing to compare against.',
  range_not_proratable:
    'This period is too long to prorate weekly contracted hours over. Choose a shorter one.',
  no_workers: 'No workers in this view.',
};

/**
 * `GET /api/dashboard/analytics/staff` answers two audiences on one URL, so
 * `staff.data` is a union: the identified `StaffSummary`, or the de-identified
 * `StaffAggregate` with `byWorker` omitted and small-N measures nulled. Every
 * absence on this screen is explained from `disclosure` rather than rendered as
 * an empty table or a bare dash — see `../disclosure.ts`.
 */
export function StaffView() {
  const { apiQuery } = useAnalyticsControls();
  const navigate = useNav();
  const [drill, setDrill] = useState<Drill | null>(null);

  const staff = useQuery({
    queryKey: ['analytics', 'staff', apiQuery],
    queryFn: () => dashboard.analyticsStaff(apiQuery),
  });

  // The same period as the figures above it (it used to be a fixed 26 weeks).
  const trends = useQuery({
    queryKey: ['analytics', 'trends', apiQuery],
    queryFn: () => dashboard.analyticsTrends(apiQuery),
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (staff.isError && isForbidden(staff.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (staff.isLoading) return <SkeletonSet surface="table" rows={5} />;
  if (staff.isError && !staff.data) {
    return (
      <LoadFailure
        error={staff.error}
        message="We couldn’t load staff analytics just now, so read this screen as unknown rather than empty."
        onRetry={() => void staff.refetch()}
      />
    );
  }
  const d = staff.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={5} />;

  const disclosure = d.disclosure;
  // Present only on the identified variant. `in` and not `?? []`: an omitted
  // roster and an empty roster mean different things and must not collapse.
  const byWorker = 'byWorker' in d ? d.byWorker : undefined;
  const rosterWithheld = isWithheld(disclosure, 'byWorker');
  // THE TIME CLOCK, OR NOTHING. Worked hours and sessions are never stood in
  // for by service hours: without a time clock they are "Not measured".
  const noClock = d.workedHoursUnavailable;
  const utilizationReason = d.utilizationUnavailableReason
    ? UTILIZATION_REASON[d.utilizationUnavailableReason]
    : null;

  const columns: Column<StaffWorkerRow>[] = [
    {
      key: 'worker',
      header: 'Worker',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.workerLabel}</span>
          {r.programName ? <span className="rn-muted"> · {r.programName}</span> : null}
          {r.employmentStatus !== 'active' ? (
            <span className="rn-muted"> · {r.employmentStatus}</span>
          ) : null}
        </span>
      ),
    },
    { key: 'scheduled', header: 'Scheduled', render: (r) => String(r.scheduled), numeric: true },
    { key: 'delivered', header: 'Delivered', render: (r) => String(r.appointments), numeric: true },
    { key: 'clients', header: 'Clients', render: (r) => String(r.clientsServed), numeric: true },
    {
      key: 'serviceHours',
      // Service time on delivered visits — never hours worked.
      header: 'Service hours',
      render: (r) => hours(r.serviceHours),
      numeric: true,
    },
    { key: 'cancelled', header: 'Cancelled', render: (r) => String(r.cancelled), numeric: true },
    { key: 'noShows', header: 'No-show', render: (r) => String(r.noShows), numeric: true },
    {
      key: 'notStarted',
      header: 'Not started',
      render: (r) => String(r.notStarted),
      numeric: true,
    },
    {
      key: 'worked',
      header: 'Worked hours',
      // Time clock only: `null` is "Not measured", never 0.0.
      render: (r) => (r.workedHours == null ? NOT_MEASURED_LABEL : hours(r.workedHours)),
      numeric: true,
    },
    {
      key: 'utilization',
      header: 'Utilization',
      render: (r) => (r.utilizationPct == null ? 'Not measurable' : `${r.utilizationPct}%`),
      numeric: true,
    },
    {
      key: 'exceptions',
      header: 'Exceptions',
      render: (r) =>
        r.openExceptions == null ? (
          NOT_MEASURED_LABEL
        ) : r.openExceptions > 0 ? (
          <Pill variant="look" dot>
            {String(r.openExceptions)}
          </Pill>
        ) : (
          '0'
        ),
    },
  ];

  return (
    <div className="an-stack">
      <DisclosureStrip disclosure={disclosure} />

      <StatGrid>
        <Stat
          label="Active workers"
          value={String(d.activeWorkers)}
          // The roster's rows are the Workers table on this screen.
          sub={`On the roster · ${d.inactiveWorkers} inactive`}
        />
        <Stat
          label="Workers delivering service"
          value={String(d.workersDelivering)}
          onOpen={() =>
            setDrill({
              title: `Workers delivering service, ${periodRangeLabel(d.period)}`,
              about: METRIC_ABOUT.workersDelivering,
              ask: `Which workers delivered service in ${askPeriod(d.period)}, and who did not?`,
              records: { set: 'delivered', from: d.period.from, to: d.period.to, scope: d.scope },
            })
          }
        />
        {noClock ? (
          <NotMeasuredStat label="Worked hours (time clock)" reason={noClock.message} />
        ) : (
          <MeasureStat
            label="Worked hours (time clock)"
            display={measure(d.workedHours, 'workedHours', disclosure, hours)}
          />
        )}
        {utilizationReason ? (
          <NotMeasuredStat label="Utilization" reason={utilizationReason} />
        ) : (
          <MeasureStat
            label="Utilization"
            display={measure(d.utilizationPct, 'utilizationPct', disclosure, (v) => `${v}%`)}
          />
        )}
        {noClock ? null : (
          <>
            <MeasureStat
              label="Closed sessions"
              display={measure(
                d.attendance?.closedSessions ?? null,
                'attendance',
                disclosure,
                String,
              )}
            />
            <MeasureStat
              label="Open sessions"
              display={measure(
                d.attendance?.openSessions ?? null,
                'attendance',
                disclosure,
                String,
              )}
            />
          </>
        )}
        <Stat label="Open exceptions" value={String(d.exceptions.open)} />
      </StatGrid>

      {drill ? <DrillPanel key={drill.title} drill={drill} onClose={() => setDrill(null)} /> : null}

      <StalenessStrip staleness={d.staleness} />

      <StaffTrendPanel trends={trends} onDrill={setDrill} />

      <Panel title="Workers" subtitle="delivery by worker — open a worker for their schedule">
        {rosterWithheld || !byWorker ? (
          <DisclosureNote
            heading="Per-worker rows are not shown"
            text={withheldReason('byWorker', 'Per-worker rows')}
          />
        ) : (
          <>
            <p className="rn-muted" style={{ margin: '0 0 var(--space-8)' }}>
              Visits with no worker recorded are in no row; they are listed under the table. A group
              visit credits every staff member on it, so with group visits the rows add up to more
              than the organization’s totals.
            </p>
            <DrilldownTable
              rows={byWorker}
              isLoading={false}
              isError={false}
              onRetry={() => void staff.refetch()}
              columns={columns}
              rowKey={(r) => r.workerId}
              onRowOpen={(r) => navigate(workerScheduleHref(r.workerId, d.period))}
              emptyHeading="No workers"
              emptyMessage="Workers appear once the directory is populated."
            />
          </>
        )}
        <NotAttributed d={d} byWorker={byWorker} />
      </Panel>
    </div>
  );
}

/**
 * THE DELIVERED VISITS NO ROW HOLDS: no worker was recorded on them, so
 * without this the rows summed short of "Services delivered" (514 of 519 in
 * production) with nothing to say why. Shown only where the server measures
 * it — for a reader narrowed to a caseload its 0 means "not yours".
 */
function NotAttributed({
  d,
  byWorker,
}: {
  d: DashboardNS.StaffResponse;
  byWorker: StaffWorkerRow[] | undefined;
}) {
  const scope = d.rowScope;
  if (scope.population === 'none' || (scope.population === 'subset' && scope.caseload)) {
    return null;
  }
  const count = d.unattributedAppointments;
  // Both absent from a server older than them: then the count stands alone.
  const wire: Partial<
    Pick<DashboardNS.StaffSummary, 'unattributedServiceHours' | 'unattributedReasons'>
  > = d;
  const unattributedHours = wire.unattributedServiceHours;
  const reasons = Object.entries(wire.unattributedReasons ?? {}) as [
    SVC.DeliveredReasonCode,
    number,
  ][];
  const rowsDelivered = byWorker?.reduce((n, r) => n + r.appointments, 0);
  const rowsHours = byWorker?.reduce((n, r) => n + r.serviceHours, 0) ?? 0;
  return (
    <section
      aria-label="Not attributed to a worker"
      style={{
        marginTop: 'var(--space-10)',
        padding: 'var(--space-10) var(--space-14)',
        border: '1px solid var(--color-line)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <p style={{ margin: 0 }}>
        <span style={{ fontWeight: 600 }}>Not attributed to a worker</span>
        {` · ${count} delivered`}
        {unattributedHours === undefined ? null : ` · ${hours(unattributedHours)} service hours`}
      </p>
      <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
        Delivered visits with no worker recorded. They count in Services delivered and Service
        hours, and belong to no row above.
      </p>
      {reasons.length > 0 ? (
        <ul className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
          {reasons.map(([code, n]) => (
            <li key={code}>
              {DELIVERED_REASON_LABELS[code] ?? code} ({n})
            </li>
          ))}
        </ul>
      ) : null}
      {rowsDelivered === undefined ? null : (
        <p style={{ margin: 'var(--space-8) 0 0' }}>
          {`Worker rows ${rowsDelivered} + not attributed ${count} = ${rowsDelivered + count} delivered`}
          {unattributedHours === undefined
            ? null
            : ` · ${hours(rowsHours + unattributedHours)} service hours`}
        </p>
      )}
    </section>
  );
}

function StaffTrendPanel({
  trends,
  onDrill,
}: {
  trends: QueryLike<DashboardNS.AnalyticsTrends>;
  onDrill: (drill: Drill) => void;
}) {
  if (trends.isLoading) return <SkeletonSet surface="band" />;
  if (trends.isError && !trends.data) {
    return (
      <TrendFailure
        error={trends.error}
        message="We couldn’t load staff trends just now."
        onRetry={() => void trends.refetch()}
      />
    );
  }
  const t = trends.data;
  if (!t) return null;
  // Hours worked is drawn only where a time clock measures it; otherwise the
  // "Worked hours" tile above already says "Not measured".
  const clocked = t.series.hours.points.some((p) => p.kind !== 'no_source');
  return (
    <div className="an-grid">
      <WeeklyPanel
        title="Workers delivering service"
        axis={t}
        about={METRIC_ABOUT.workersDelivering}
        onDrill={onDrill}
        lines={[
          {
            name: 'Workers delivering service',
            color: 'var(--chart-4)',
            series: t.series.workersDelivering,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Service hours delivered"
        axis={t}
        about={METRIC_ABOUT.serviceHours}
        additive
        formatValue={(v) => formatNumber(v, Number.isInteger(v) ? 0 : 1)}
        onDrill={onDrill}
        lines={[
          {
            name: 'Service hours delivered',
            color: 'var(--chart-1)',
            series: t.series.serviceHours,
            set: 'delivered',
          },
        ]}
      />
      {clocked ? (
        <WeeklyPanel
          title="Hours worked (time clock)"
          axis={t}
          about={METRIC_ABOUT.hoursWorked}
          additive
          formatValue={(v) => formatNumber(v, Number.isInteger(v) ? 0 : 1)}
          lines={[{ name: 'Hours worked', color: 'var(--chart-1)', series: t.series.hours }]}
        />
      ) : null}
    </div>
  );
}
